import Modal from 'react-modal';
import { UserProvider } from './contexts/User';
import { KeycloakAuthProvider } from './contexts/KeycloakAuth';
import Routes from './routes/Routes';
import { startVersionPolling } from './utils/checkVersion';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import configs from './configs';
import { useEffect } from 'react';

export const initDatadog = (runtimeConfig = configs) => {
  import('@datadog/browser-rum').then(({ datadogRum }) => {
    datadogRum.init({
      clientToken:
        runtimeConfig.env === 'prod'
          ? 'pub28b0ffd72bf254a71863b24326de892e'
          : 'pub9b55a102614c99899cc50a0af2dca2d9',
      applicationId:
        runtimeConfig.env === 'prod'
          ? 'de49bb2f-58a1-43d9-ab71-90e76465c2ea'
          : '707aaa76-7b2b-4fdd-8de7-bb3c27a4821b',
      site: 'datadoghq.com',
      service:
        runtimeConfig.env === 'prod' ? 'sensory-one-[prod]' : 'sensory-one-[qa]',
      env: runtimeConfig.env === 'prod' ? 'PROD' : 'qa',
      version: localStorage.getItem('version') || '',
      sessionSampleRate: 20,
      sessionReplaySampleRate: 10,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: false,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: event => {
        if (event.type === 'error') {
          datadogRum.startSessionReplayRecording({ force: true });
        }
        return true;
      },
    });
  });
};

export const scheduleDatadog = (
  environment = configs.env,
  targetWindow = window,
  schedule = setTimeout,
) => {
  if (environment !== 'prod') return;
  if ('requestIdleCallback' in targetWindow) {
    targetWindow.requestIdleCallback(() => initDatadog(), { timeout: 5000 });
  } else {
    schedule(() => initDatadog(), 3000);
  }
};

const App = ({ runtimeEnvironment = process.env.NODE_ENV }) => {
  if (runtimeEnvironment !== 'test') {
    Modal.setAppElement('#root');
  }

  useEffect(() => {
    if (window.__appLoaded) {
      window.__appLoaded();
    }

    // The login screen performs the pre-login version/configuration check.
    // Running another check immediately here can finish after authentication
    // and reload the freshly rendered home page when the versions differ.
    // Start version monitoring after the app is mounted instead; this keeps
    // the login -> home transition stable while still detecting later updates.
    const stopVersionPolling = startVersionPolling(undefined, {
      immediate: false,
    });

    scheduleDatadog();

    return () => {
      stopVersionPolling();
    };
  }, []);

  return (
    <ChunkErrorBoundary>
      <KeycloakAuthProvider>
        <UserProvider>
          <Routes />
        </UserProvider>
      </KeycloakAuthProvider>
    </ChunkErrorBoundary>
  );
};

export default App;
